import { MAP_IDS, type BandCounts, type MapId, type MapPreset } from '../types';

/** Decoration counts per player band, the same on every map. */
const DECOR_COUNTS: BandCounts = [10, 14, 18, 22];

/**
 * Map presets (DROW-71 spec). `startRadius` is tuned for 8 players and scaled by player count
 * at round start; square maps use ~0.89× the radius to keep the circle's area.
 */
export const MAP_PRESETS: Readonly<Record<MapId, MapPreset>> = {
  emberRing: {
    id: 'emberRing',
    name: 'Ember Ring',
    hint: 'open caldera ring, the classic duel',
    skin: 'calderaRock',
    // Ice, grass and volcanic rock wedges, turned by the round seed (DROW-188).
    terrain: { blend: ['iceSheet', 'grass'], layout: 'sectors' },
    arena: {
      arenaShape: 'circle',
      startRadius: 900,
      graceMs: 15_000,
      shrinkIntervalMs: 10_000,
      shrinkPct: 0.04,
      lavaDpsPct: 0.08,
      frictionMult: 1,
    },
    // Stays the open duel map; decoration only.
    props: { decor: { theme: 'caldera', counts: DECOR_COUNTS } },
  },
  frostmoatYard: {
    id: 'frostmoatYard',
    name: 'Frostmoat Yard',
    hint: 'ice: knockback slides ~3× farther',
    skin: 'frostCobble',
    terrain: { blend: ['snow'] },
    arena: {
      arenaShape: 'square',
      startRadius: 800,
      graceMs: 12_000,
      shrinkIntervalMs: 9_000,
      shrinkPct: 0.045,
      lavaDpsPct: 0.1,
      frictionMult: 0.3,
    },
    // No frozen pools: on an ice floor they'd hit the friction floor and do nothing. One crystal
    // hideout at every lobby size; it takes the centre of the small pits, so 2–6 player lobbies
    // get one blocker fewer (with two, a 2-player pit fit both only ~60% of the time).
    props: {
      blockers: { variants: ['crystal'], counts: [1, 2, 4, 5] },
      hideouts: { look: 'crystal', counts: [1, 1, 1, 1] },
      decor: { theme: 'frostCobble', counts: DECOR_COUNTS },
    },
  },
  crucible: {
    id: 'crucible',
    name: 'The Crucible',
    hint: 'small pit, fast shrink, hot lava',
    skin: 'scorchedDirt',
    terrain: { blend: ['obsidianGrit'] },
    arena: {
      arenaShape: 'circle',
      startRadius: 700,
      graceMs: 8_000,
      shrinkIntervalMs: 6_000,
      shrinkPct: 0.05,
      lavaDpsPct: 0.12,
      frictionMult: 1,
    },
    // Small, fast pit: no clutter.
    props: { decor: { theme: 'scorchedDirt', counts: DECOR_COUNTS } },
  },
  standingStones: {
    id: 'standingStones',
    name: 'Standing Stones',
    hint: 'stone pillars block movement; they sink as the lava arrives',
    skin: 'mossyRuin',
    terrain: { blend: ['grass'] },
    arena: {
      arenaShape: 'circle',
      startRadius: 1000,
      graceMs: 15_000,
      shrinkIntervalMs: 10_000,
      shrinkPct: 0.045,
      lavaDpsPct: 0.08,
      frictionMult: 1,
    },
    // Two pillars in small lobbies, four from 4 players. No centre pillar.
    obstacles: [
      { angleDeg: 45, distFrac: 0.45, r: 50 },
      { angleDeg: 225, distFrac: 0.45, r: 50 },
      { angleDeg: 135, distFrac: 0.45, r: 50, minPlayers: 4 },
      { angleDeg: 315, distFrac: 0.45, r: 50, minPlayers: 4 },
    ],
    // Keeps its fixed pillars; one frozen pool and mud from 4 players, ruin hideouts. The pillars
    // crowd the hideout band, so hideouts keep a tighter 80 u gap (at 120 u a 4-player round fits
    // a cluster only ~10% of the time; 80 u still leaves a warlock-wide lane).
    props: {
      zones: [
        { kind: 'frozenPool', counts: [0, 1, 1, 1] },
        { kind: 'mudPatch', counts: [0, 1, 1, 2] },
      ],
      hideouts: { look: 'ruin', gap: 80 },
      decor: { theme: 'mossyRuin', counts: DECOR_COUNTS },
    },
  },
  frozenRuins: {
    id: 'frozenRuins',
    name: 'Frozen Ruins',
    hint: 'random ruins and frozen pools: knockback slides far on the ice',
    skin: 'frozenRuin',
    terrain: { blend: ['snow'] },
    arena: {
      arenaShape: 'circle',
      startRadius: 1000,
      graceMs: 15_000,
      shrinkIntervalMs: 10_000,
      shrinkPct: 0.045,
      lavaDpsPct: 0.08,
      frictionMult: 1,
    },
    props: {
      blockers: { variants: ['stone', 'column'], counts: [2, 3, 4, 5] },
      zones: [{ kind: 'frozenPool', counts: [1, 1, 2, 3] }],
      hideouts: { look: 'ruin' },
      decor: { theme: 'frozenRuin', counts: DECOR_COUNTS },
    },
  },
  verdantMire: {
    id: 'verdantMire',
    name: 'Verdant Mire',
    hint: 'mud bogs slow you but hold you in place; ruins in the middle',
    skin: 'verdantMire',
    terrain: { blend: ['mudDirt', 'grass'] },
    arena: {
      arenaShape: 'circle',
      startRadius: 950,
      graceMs: 15_000,
      shrinkIntervalMs: 10_000,
      shrinkPct: 0.045,
      lavaDpsPct: 0.08,
      frictionMult: 1,
    },
    // Mud-heavy with a little ice from 7 players; ruin hideouts. Reuses the mossy ruin decor kit.
    props: {
      zones: [
        { kind: 'mudPatch', counts: [1, 2, 2, 3] },
        { kind: 'frozenPool', counts: [0, 0, 1, 1] },
      ],
      hideouts: { look: 'ruin' },
      decor: { theme: 'mossyRuin', counts: DECOR_COUNTS },
    },
  },
};

export function isMapId(value: unknown): value is MapId {
  return (MAP_IDS as readonly unknown[]).includes(value);
}
